/**
 * Firestore wrappers for common read/write/subscribe operations.
 *
 * **Read / write:** `getDoc` / `getDocs` for reads, `setDoc` /
 * `updateDoc` / `deleteDoc` for writes.
 *
 * **Real-time subscribe:** `subscribeCollection` / `subscribeQuery` for
 * collection-level `onSnapshot` subscriptions.
 *
 * **CRITICAL:** Do NOT import or use `onSnapshot` from `firebase/firestore`
 * directly in page/component code. Always use the wrappers in this module.
 */

import {
  doc,
  getDoc,
  getDocs,
  collection,
  setDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  startAt,
  endAt,
  documentId,
  onSnapshot,
  runTransaction,
  type DocumentData,
  type DocumentSnapshot,
  type QueryDocumentSnapshot,
  type QuerySnapshot,
  type QueryConstraint,
  type WhereFilterOp,
  type OrderByDirection,
  type Unsubscribe,
} from 'firebase/firestore';
import { firestore } from './client';

/**
 * Fetch a single document.
 *
 * @param path         Root collection path (e.g. `'users'`).
 * @param pathSegments Additional path segments (document id, sub-collection, …).
 */
export async function getDocument<T = DocumentData>(
  path: string,
  ...pathSegments: string[]
): Promise<DocumentSnapshot<T>> {
  if (!firestore) throw new Error('Firestore not initialised');
  const docRef = doc(firestore, path, ...pathSegments);
  return getDoc(docRef) as Promise<DocumentSnapshot<T>>;
}

/**
 * Fetch all documents in a collection.
 *
 * @param path         Root collection path.
 * @param pathSegments Additional path segments for nested collections.
 */
export async function getCollection<T = DocumentData>(
  path: string,
  ...pathSegments: string[]
): Promise<QuerySnapshot<T>> {
  if (!firestore) throw new Error('Firestore not initialised');
  const collectionRef = collection(firestore, path, ...pathSegments);
  return getDocs(collectionRef) as Promise<QuerySnapshot<T>>;
}

/** Create or overwrite a document. */
export async function setDocument(
  path: string,
  data: DocumentData,
  ...pathSegments: string[]
): Promise<void> {
  if (!firestore) throw new Error('Firestore not initialised');
  const docRef = doc(firestore, path, ...pathSegments);
  await setDoc(docRef, data);
}

/**
 * Add a document to a collection (auto-generated ID).
 *
 * @param data        The document data.
 * @param path        Root collection path.
 * @param pathSegments Additional path segments for nested collections.
 * @returns           The new document's auto-generated id.
 */
export async function addDocument(
  data: DocumentData,
  path: string,
  ...pathSegments: string[]
): Promise<string> {
  if (!firestore) throw new Error('Firestore not initialised');
  const collectionRef = collection(firestore, path, ...pathSegments);
  const docRef = await addDoc(collectionRef, data);
  return docRef.id;
}

/** Merge fields into an existing document. */
export async function updateDocument(
  path: string,
  data: Partial<DocumentData>,
  ...pathSegments: string[]
): Promise<void> {
  if (!firestore) throw new Error('Firestore not initialised');
  const docRef = doc(firestore, path, ...pathSegments);
  await updateDoc(docRef, data);
}

/** Delete a document. */
export async function deleteDocument(path: string, ...pathSegments: string[]): Promise<void> {
  if (!firestore) throw new Error('Firestore not initialised');
  const docRef = doc(firestore, path, ...pathSegments);
  await deleteDoc(docRef);
}

/**
 * Subscribe to a collection with real-time updates via onSnapshot.
 *
 * @param path         Root collection path.
 * @param pathSegments Additional path segments for nested collections.
 * @param callback     Called with the snapshot every time the collection changes.
 * @returns            An unsubscribe function.
 */
export function subscribeCollection<T = DocumentData>(
  path: string,
  ...pathSegments: string[]
): { onSnapshot: (callback: (snapshot: QuerySnapshot<T>) => void) => Unsubscribe } {
  if (!firestore) throw new Error('Firestore not initialised');
  const collectionRef = collection(firestore, path, ...pathSegments);
  return {
    onSnapshot: (callback: (snapshot: QuerySnapshot<T>) => void) => {
      return onSnapshot(collectionRef as any, callback);
    },
  };
}

/**
 * Subscribe to a query with real-time updates via onSnapshot.
 * Useful when you need where/orderBy constraints.
 *
 * @param collectionPath  Root collection path.
 * @param constraints     Array of `{ field, op, value }` filter constraints.
 * @param orderByFields   Optional array of `{ field, dir }` sort specifications.
 * @param callback        Called with the snapshot every time results change.
 * @returns               An unsubscribe function.
 */
export function subscribeQuery<T = DocumentData>(
  collectionPath: string,
  constraints: Array<{ field: string; op: WhereFilterOp; value: unknown }>,
  orderByFields?: Array<{ field: string; dir?: OrderByDirection }>,
): {
  onSnapshot: (
    callback: (snapshot: QuerySnapshot<T>) => void,
    onError?: (error: Error) => void,
  ) => Unsubscribe;
} {
  if (!firestore) throw new Error('Firestore not initialised');

  const queryConstraints: QueryConstraint[] = constraints.map((c) => where(c.field, c.op, c.value));
  if (orderByFields) {
    orderByFields.forEach((ob) => queryConstraints.push(orderBy(ob.field, ob.dir || 'asc')));
  }

  const collectionRef = collection(firestore, collectionPath);
  const q = query(collectionRef, ...queryConstraints);

  return {
    onSnapshot: (
      callback: (snapshot: QuerySnapshot<T>) => void,
      onError?: (error: Error) => void,
    ) => {
      if (onError) {
        return onSnapshot(q as any, callback, onError);
      }
      return onSnapshot(q as any, callback);
    },
  };
}

/**
 * Transactionally upsert one entry in users/{uid}.voicePlans.
 * Re-reads the latest array inside the transaction so a stale client
 * snapshot cannot wipe sibling plans.
 */
export async function upsertUserVoicePlanEntry(
  uid: string,
  voicePlanId: string,
  entry: Record<string, unknown>,
): Promise<void> {
  if (!firestore) throw new Error('Firestore not initialised');
  const userRef = doc(firestore, 'users', uid);
  await runTransaction(firestore, async (transaction) => {
    const snap = await transaction.get(userRef);
    if (!snap.exists()) {
      throw new Error('User document not found');
    }
    const data = snap.data() || {};
    const current = Array.isArray(data.voicePlans) ? [...data.voicePlans] : [];
    const idx = current.findIndex((vp: { id?: string }) => vp?.id === voicePlanId);
    if (idx >= 0) {
      current[idx] = { ...current[idx], ...entry };
    } else {
      current.push(entry);
    }
    transaction.update(userRef, { voicePlans: current });
  });
}

/**
 * Run a query with filters, optional ordering, and optional limit.
 *
 * @param collectionPath  Root collection path.
 * @param constraints     Array of `{ field, op, value }` filter constraints.
 * @param orderByField    Optional field to order results by.
 * @param orderDir        Sort direction (`'asc'` or `'desc'`; default `'asc'`).
 * @param limitCount      Optional maximum number of results.
 */
export async function queryCollection<T = DocumentData>(
  collectionPath: string,
  constraints: Array<{ field: string; op: WhereFilterOp; value: unknown }>,
  orderByField?: string,
  orderDir?: OrderByDirection,
  limitCount?: number,
  startAfterDoc?: QueryDocumentSnapshot<DocumentData>,
): Promise<QuerySnapshot<T>> {
  if (!firestore) throw new Error('Firestore not initialised');

  const queryConstraints: QueryConstraint[] = constraints.map((c) => where(c.field, c.op, c.value));

  if (orderByField) {
    queryConstraints.push(orderBy(orderByField, orderDir || 'asc'));
  }
  if (startAfterDoc) {
    queryConstraints.push(startAfter(startAfterDoc));
  }
  if (limitCount !== undefined) {
    queryConstraints.push(limit(limitCount));
  }

  const collectionRef = collection(firestore, collectionPath);
  const q = query(collectionRef, ...queryConstraints);
  return getDocs(q) as Promise<QuerySnapshot<T>>;
}

export type DocumentIdQueryOptions = {
  direction?: OrderByDirection;
  startAfterId?: string;
  startAtId?: string;
  endAtId?: string;
  limitCount?: number;
};

/**
 * Paginate a collection by document id (1 read per `limitCount`).
 * Used for editable-exercise prev/next and modest chapter prefix pages.
 */
export async function queryByDocumentId<T = DocumentData>(
  collectionPath: string,
  options: DocumentIdQueryOptions = {},
): Promise<QuerySnapshot<T>> {
  if (!firestore) throw new Error('Firestore not initialised');

  const queryConstraints: QueryConstraint[] = [
    orderBy(documentId(), options.direction || 'asc'),
  ];
  if (options.startAtId !== undefined) {
    queryConstraints.push(startAt(options.startAtId));
  }
  if (options.startAfterId !== undefined) {
    queryConstraints.push(startAfter(options.startAfterId));
  }
  if (options.endAtId !== undefined) {
    queryConstraints.push(endAt(options.endAtId));
  }
  if (options.limitCount !== undefined) {
    queryConstraints.push(limit(options.limitCount));
  }

  const collectionRef = collection(firestore, collectionPath);
  const q = query(collectionRef, ...queryConstraints);
  return getDocs(q) as Promise<QuerySnapshot<T>>;
}
